
import { type Dispatch } from 'redux';

import { appNavigate, maybeRedirectToWelcomePage } from '../app/actions';
import {
    conferenceWillJoin,
    getCurrentConference,
    sendLocalParticipant,
    setPassword
} from '../base/conference';
import { hideDialog, openDialog } from '../base/dialog';
import { getLocalParticipant, isLocalParticipantModerator } from '../base/participants';
import { checkIfReactNative } from '../base/util/checkOS';
export * from './actions.any';


import {
    DISABLE_LOBBY_SOUND,
    KNOCKING_PARTICIPANT_ARRIVED_OR_UPDATED,
    KNOCKING_PARTICIPANT_LEFT,
    OPEN_LOBBY,
    SET_KNOCKING_STATE,
    SET_LOBBY_MODE_ENABLED,
    SET_PASSWORD_JOIN_FAILED
} from './actionTypes';
import { admitAllLobbyParticipants } from './actions.any';
import { getLobbyAdmissionRooms } from './functions';
import logger from './logger';
declare var APP: Object;

const LOBBY_SOUND_PREFERENCE_MESSAGE_TYPE = 'lobby-sound-preference';

/**
 * Cancels the ongoing knocking and abandones the join flow.
 *
 * @returns {Function}
 */
export function cancelKnocking() {
    // eslint-disable-next-line require-await -- async by contract: callers rely on the returned promise
    return async (dispatch: Dispatch<any>) => {
        if (typeof APP !== 'undefined') {
            // when we are redirecting the library should handle any
            // unload and clean of the connection.
            APP.API.notifyReadyToClose();
            dispatch(maybeRedirectToWelcomePage());

            return;
        }

        dispatch(appNavigate(undefined));
    };
}

/**
 * Action to hide the lobby screen.
 *
 * @returns {hideDialog}
 */
export function hideLobbyScreen() {
    const { LobbyScreen } = require('./components');

    return hideDialog(LobbyScreen);
}

/**
 * Tries to join with a preset password.
 *
 * @param {string} password - The password to join with.
 * @returns {Function}
 */
export function joinWithPassword(password: string) {
    // eslint-disable-next-line require-await -- async by contract: callers rely on the returned promise
    return async (dispatch: Dispatch<any>, getState: Function) => {
        const conference = getCurrentConference(getState);

        dispatch(setPassword(conference, conference.join, password));
    };
}

/**
 * Action to be dispatched when a knocking poarticipant leaves before any response.
 *
 * @param {string} id - The ID of the participant.
 * @returns {{
 *     id: string,
 *     type: KNOCKING_PARTICIPANT_LEFT
 * }}
 */
export function knockingParticipantLeft(id: string) {
    return {
        id,
        type: KNOCKING_PARTICIPANT_LEFT
    };
}

/**
 * Action to open the lobby screen.
 *
 * @param {boolean} prejoin -  Whether to open the lobby screen.
 * @returns {openDialog}
 */
export function openLobbyScreen(prejoin: boolean) {
    return {
        type: OPEN_LOBBY,
        lobbyscreen: {
            _isLobbyScreenVisible: true,
            _isPrejoin: prejoin
        }
    };
}


/**
 * Action to be executed when a participant starts knocking or an already knocking participant gets updated.
 *
 * @param {Object} participant - The knocking participant.
 * @returns {{
 *     participant: Object,
 *     type: KNOCKING_PARTICIPANT_ARRIVED_OR_UPDATED
 * }}
 */
export function participantIsKnockingOrUpdated(participant: Object) {
    return {
        participant,
        type: KNOCKING_PARTICIPANT_ARRIVED_OR_UPDATED
    };
}

/**
 * Approves (lets in) or rejects a knocking participant.
 *
 * @param {string} id - The id of the knocking participant.
 * @param {boolean} approved - True if the participant is approved, false otherwise.
 * @returns {Function}
 */
export function setKnockingParticipantApproval(id: string, approved: boolean) {
    // eslint-disable-next-line require-await -- async by contract: callers rely on the returned promise
    return async (dispatch: Dispatch<any>, getState: Function) => {
        const conference = getCurrentConference(getState);

        if (conference) {
            if (approved) {
                if (navigator.product === 'ReactNative') {
                    conference.lobbyApproveAccess(id);
                } else {
                    dispatch(showLobbyAdmissionDialog([ { id } ]));
                }
            } else {
                conference.lobbyDenyAccess(id);
            }
        }
    };
}

/**
 * Approves the request of a knocking participant to join the meeting.
 *
 * @param {string} id - The id of the knocking participant.
 * @returns {Function}
 */
export function approveKnockingParticipant(id: string) {
    return (dispatch: Dispatch<any>) => {
        dispatch(showLobbyAdmissionDialog([ { id } ]));
    };
}

/**
 * Opens the room picker used by single and bulk lobby admission.
 *
 * @param {Array<Object>} participants - Lobby participants to admit.
 * @param {Function} [onAdmissionComplete] - Optional work to run after admission.
 * @returns {Function}
 */
export function showLobbyAdmissionDialog(participants: Array<Object>, onAdmissionComplete?: Function) {
    return (dispatch: Dispatch<any>, getState: Function) => {
        if (!participants?.length) {
            return;
        }

        const rooms = getLobbyAdmissionRooms(getState());

        // Nothing to pick from - don't make the moderator confirm a choice that
        // doesn't exist. This keeps a plain meeting's admit flow one click.
        // The picker is web-only (actions.native re-exports this file), so on
        // mobile the meeting stays the only destination.
        if (rooms.length < 2 || checkIfReactNative()) {
            dispatch(admitAllLobbyParticipants(participants));
            onAdmissionComplete?.();

            return;
        }

        const LobbyAdmissionDialog = require('./components/LobbyAdmissionDialog').default;

        dispatch(openDialog(LobbyAdmissionDialog, {
            onAdmissionComplete,
            participants
        }));
    };
}

/**
 * Denies the request of a knocking participant to join the meeting.
 *
 * @param {string} id - The id of the knocking participant.
 * @returns {Function}
 */
export function rejectKnockingParticipant(id: string) {
    return (dispatch: Dispatch<any>, getState: Function) => {
        const conference = getCurrentConference(getState);

        conference && conference.lobbyDenyAccess(id);
    };
}

/**
 * Action to set the knocking state of the participant.
 *
 * @param {boolean} knocking - The new state.
 * @returns {{
 *     state: boolean,
 *     type: SET_KNOCKING_STATE
 * }}
 */
export function setKnockingState(knocking: boolean) {
    return {
        knocking,
        type: SET_KNOCKING_STATE
    };
}

/**
 * Action to set the new state of the lobby mode.
 *
 * @param {boolean} enabled - The new state to set.
 * @returns {{
 *     enabled: boolean,
 *     type: SET_LOBBY_MODE_ENABLED
 * }}
 */
export function setLobbyModeEnabled(enabled: boolean) {
    return {
        enabled,
        type: SET_LOBBY_MODE_ENABLED
    };
}

/**
 * Action to be dispatched when we failed to join with a password.
 *
 * @param {boolean} failed - True of recent password join failed.
 * @returns {{
 *     failed: boolean,
 *     type: SET_PASSWORD_JOIN_FAILED
 * }}
 */
export function setPasswordJoinFailed(failed: boolean) {
    return {
        failed,
        type: SET_PASSWORD_JOIN_FAILED
    };
}

/**
 * Starts knocking and waiting for approval.
 *
 * @returns {Function}
 */
export function startKnocking() {
    // eslint-disable-next-line require-await -- async by contract: callers rely on the returned promise
    return async (dispatch: Dispatch<any>, getState: Function) => {
        const state = getState();
        const { membersOnly } = state['features/base/conference'];
        const localParticipant = getLocalParticipant(state);

        dispatch(conferenceWillJoin(membersOnly));

        // We need to update the conference object with the current display name, if approved
        // we want to send that display name, it was not updated in case when pre-join is disabled
        sendLocalParticipant(state, membersOnly);

        // Rejects when the server's members-only error carried no lobby room JID; knocking can't work then.
        membersOnly.joinLobby(localParticipant.name, localParticipant.email).catch(error => {
            logger.error('Failed to join the lobby room', error);
            dispatch(setKnockingState(false));
        });
        dispatch(setKnockingState(true));
    };
}

/**
 * Disables the lobby sound.
 *
 * @param {?boolean} disabledLobbySound - Explicit sound disabled state. If omitted, toggles current state.
 * @param {Object} options - Additional options.
 * @param {boolean} options.broadcast - Whether to broadcast to other moderators.
 * @returns {Function}
 */
export function disableLobbySound(disabledLobbySound: ?boolean, options: Object = {}) {
    return (dispatch: Dispatch<any>, getState: Function) => {
        const state = getState();
        const nextDisabledLobbySound = typeof disabledLobbySound === 'boolean'
            ? disabledLobbySound
            : !state['features/lobby'].disabledLobbySound;

        dispatch({
            type: DISABLE_LOBBY_SOUND,
            disabledLobbySound: nextDisabledLobbySound
        });

        if (options.broadcast === false || !isLocalParticipantModerator(state)) {
            return;
        }

        const conference = getCurrentConference(getState);

        if (conference && conference.isJoined()) {
            try {
                conference.sendEndpointMessage('', {
                    type: LOBBY_SOUND_PREFERENCE_MESSAGE_TYPE,
                    disabledLobbySound: nextDisabledLobbySound
                });
            } catch {
                // Best-effort broadcast only. Keep the local preference change even
                // when data channels are unavailable in single-moderator rooms.
            }
        }
    };
}
